import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/useRest";

export const useModusOperandiStore = defineStore("modusOperandi", () => {
  const modusOperandi = ref([]);
  const allModusOperandi = ref([]);
  const allModusOperandiLoaded = ref(false);
  const catalogNeedsRefresh = ref(false);
  const isLoading = ref(false);
  const pageSize = ref(6);
  const currentPage = ref(1);
  const totalModusOperandi = ref(0);

  const rest = useRest();

  const BASE_URL = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/parametros/tipo-modus-operandi`;

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);
  const totalPages = computed(() => Math.ceil(totalModusOperandi.value / pageSize.value));

  async function getTotal(searchQuery = null) {
    try {
      let url = `${BASE_URL}?getTotal=true`;
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const { data } = await rest.get({ url });
      totalModusOperandi.value = +data.doc.total;
    } catch (e) {
      console.error("getTotal>>", e);
    }
  }

  async function setAllModusOperandi(refresh = false) {
    try {
      const shouldRefresh = refresh || catalogNeedsRefresh.value;
      if (allModusOperandiLoaded.value && !shouldRefresh) {
        return allModusOperandi.value;
      }
      const { data } = await rest.get({ url: BASE_URL });
      allModusOperandi.value = data.data;
      allModusOperandiLoaded.value = true;
      catalogNeedsRefresh.value = false;
      return allModusOperandi.value;
    } catch (e) {
      console.error("setAllModusOperandi>>", e);
      return allModusOperandi.value;
    }
  }

  async function setModusOperandi(page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const limit = pageSize.value;
      const offsetValue = (page - 1) * limit;
      const url = `${BASE_URL}?limit=${limit}&offset=${offsetValue}`;
      const { data } = await rest.get({ url });
      modusOperandi.value = data.data;
    } catch (e) {
      console.error("setModusOperandi>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function searchModusOperandi(searchQuery, page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const limit = pageSize.value;
      const offsetValue = (page - 1) * limit;
      const url = `${BASE_URL}?limit=${limit}&offset=${offsetValue}&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      modusOperandi.value = data.data;
    } catch (e) {
      console.error("searchModusOperandi>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function createModusOperandi(payload) {
    try {
      const { data } = await rest.post({ url: BASE_URL, formData: payload });
      allModusOperandiLoaded.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("createModusOperandi>>", e);
      throw e;
    }
  }

  async function updateModusOperandi(id, payload) {
    try {
      const { data } = await rest.put({ url: `${BASE_URL}/${id}`, formData: payload });
      allModusOperandiLoaded.value = false;
      catalogNeedsRefresh.value = true;
      return data;
    } catch (e) {
      console.error("updateModusOperandi>>", e);
      throw e;
    }
  }

  async function deleteModusOperandi(id) {
    try {
      const { data } = await rest.del({ url: `${BASE_URL}/${id}` });
      allModusOperandiLoaded.value = false;
      catalogNeedsRefresh.value = true;
      await getTotal();
      await setModusOperandi(currentPage.value);
      return data;
    } catch (e) {
      console.error("deleteModusOperandi>>", e);
      throw e;
    }
  }

  function handlePageChange(event) {
    const page = Math.floor(event.first / pageSize.value) + 1;
    goToPage(page);
  }

  function goToPage(page) {
    currentPage.value = page;
    setModusOperandi(page);
  }

  function prevPage() {
    if (currentPage.value > 1) goToPage(currentPage.value - 1);
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) goToPage(currentPage.value + 1);
  }

  return {
    modusOperandi,
    allModusOperandi,
    isLoading,
    pageSize,
    currentPage,
    offset,
    totalModusOperandi,
    totalPages,
    setAllModusOperandi,
    setModusOperandi,
    searchModusOperandi,
    getTotal,
    createModusOperandi,
    updateModusOperandi,
    deleteModusOperandi,
    handlePageChange,
    goToPage,
    prevPage,
    nextPage,
  };
});
